import type { Permission, UserRole } from '@bcl/shared';
import { hasPermission } from '@bcl/shared';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/lib/auth-context';
import { AdminAssistancePage } from '@/pages/admin/admin-assistance-page';
import { AdminAuditPage } from '@/pages/admin/admin-audit-page';
import { AdminClientsPage } from '@/pages/admin/admin-clients-page';
import { AdminDocumentsPage } from '@/pages/admin/admin-documents-page';
import { AdminOverviewPage } from '@/pages/admin/admin-overview-page';
import { AdminClientDetailPage } from '@/pages/admin/admin-client-detail-page';
import { AdminPacksPage } from '@/pages/admin/admin-packs-page';
import { AdminProjectDetailPage } from '@/pages/admin/admin-project-detail-page';
import { AdminPaymentsPage } from '@/pages/admin/admin-payments-page';
import { AdminPlanningPage } from '@/pages/admin/admin-planning-page';
import { AdminProjectsPage } from '@/pages/admin/admin-projects-page';
import { AdminAppointmentsPage } from '@/pages/admin/admin-appointments-page';
import { AdminInterventionsPage } from '@/pages/admin/admin-interventions-page';
import { AdminLeadsPage } from '@/pages/admin/admin-leads-page';
import { AdminPartnerInvoicesPage } from '@/pages/admin/admin-partner-invoices-page';
import { AdminPartnersPage } from '@/pages/admin/admin-partners-page';
import { AdminPartnerDetailPage } from '@/pages/admin/admin-partner-detail-page';
import { AdminStaffPage } from '@/pages/admin/admin-staff-page';
import { AdminUsersPage } from '@/pages/admin/admin-users-page';
import { PartnerAssistancePage } from '@/pages/partner/partner-assistance-page';
import { PartnerInterventionsPage } from '@/pages/partner/partner-interventions-page';
import { PartnerLeadsPage } from '@/pages/partner/partner-leads-page';
import { PartnerProfilePage } from '@/pages/partner/partner-profile-page';
import { DashboardAssistancePage } from '@/pages/dashboard-assistance-page';
import { DashboardDocumentsPage } from '@/pages/dashboard-documents-page';
import { DashboardFacturesPage } from '@/pages/dashboard-factures-page';
import { DashboardMessagesPage } from '@/pages/dashboard-messages-page';
import { DashboardOffresPage } from '@/pages/dashboard-offres-page';
import { DashboardPage } from '@/pages/dashboard-page';
import { DashboardRequestsPage } from '@/pages/dashboard-requests-page';
import { LandingPage } from '@/pages/landing';
import { LoginPage } from '@/pages/login-page';
import { AboutPage } from '@/pages/marketing/about-page';
import { ContactPage } from '@/pages/marketing/contact-page';
import { FaqPage } from '@/pages/marketing/faq-page';
import { OffresPage } from '@/pages/marketing/offres-page';
import { ProjetPage } from '@/pages/marketing/projet-page';
import { ServicesPage } from '@/pages/marketing/services-page';
import {
  AssistancePage,
  CookiesPage,
  OffresEntreprisePage,
  OffresFibrePage,
  OffresPromotionsPage,
  PrivacyPage,
  StatusPage,
  TermsPage,
} from '@/pages/marketing/static-page';
import { AppointmentConfirmPage } from '@/pages/appointment-confirm-page';
import { DashboardRendezVousPage } from '@/pages/dashboard-rendez-vous-page';
import { NewProjectPage } from '@/pages/new-project-page';
import { ProjectDetailPage } from '@/pages/project-detail-page';
import { ProjectWizardPage } from '@/pages/project-wizard-page';
import { SignupPage } from '@/pages/signup-page';
import { AssigneeAppointmentsPage } from '@/pages/technician/technician-appointments-page';
import { TechnicianAssistancePage } from '@/pages/technician/technician-assistance-page';
import { TechnicianDocumentsPage } from '@/pages/technician/technician-documents-page';
import { TechnicianOverviewPage } from '@/pages/technician/technician-overview-page';
import { TechnicianPlanningPage } from '@/pages/technician/technician-planning-page';
import { TechnicianProjectsPage } from '@/pages/technician/technician-projects-page';
import { TechnicianReportsPage } from '@/pages/technician/technician-reports-page';

const queryClient = new QueryClient();

function AuthLoadingScreen() {
  return (
    <div className="grid min-h-screen place-items-center bg-bcl-grey">
      <p className="text-sm text-gray-500">Chargement de votre espace…</p>
    </div>
  );
}

function RequireAuth({ children }: { children: JSX.Element }) {
  const { user, isLoading } = useAuth();
  if (isLoading) return <AuthLoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function RequireRole({
  children,
  roles,
  permission,
}: {
  children: JSX.Element;
  roles: UserRole[];
  permission?: Permission;
}) {
  const { user, isLoading } = useAuth();
  if (isLoading) return <AuthLoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (!roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  // Permission granulaire : un admin sans le droit est renvoyé vers l'accueil
  // back-office (le super_admin a tout par son rôle).
  if (permission && !hasPermission(user.role, user.permissions, permission)) {
    return <Navigate to="/admin" replace />;
  }
  return children;
}

export function AppRoutes() {
  return (
    <Routes>
      {/* Public marketing */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/offres" element={<OffresPage />} />
      <Route path="/offres/fibre" element={<OffresFibrePage />} />
      <Route path="/offres/entreprise" element={<OffresEntreprisePage />} />
      <Route path="/offres/promotions" element={<OffresPromotionsPage />} />
      <Route path="/services" element={<ServicesPage />} />
      <Route path="/le-projet" element={<ProjetPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/faq" element={<FaqPage />} />
      <Route path="/assistance" element={<AssistancePage />} />
      <Route path="/status" element={<StatusPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/cookies" element={<CookiesPage />} />

      {/* Auth */}
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/rdv/confirmer" element={<AppointmentConfirmPage />} />

      {/* App */}
      <Route
        path="/dashboard"
        element={
          <RequireAuth>
            <DashboardPage />
          </RequireAuth>
        }
      />
      <Route
        path="/dashboard/documents"
        element={
          <RequireAuth>
            <DashboardDocumentsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/dashboard/offres"
        element={
          <RequireAuth>
            <DashboardOffresPage />
          </RequireAuth>
        }
      />
      <Route
        path="/dashboard/requests"
        element={
          <RequireAuth>
            <DashboardRequestsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/dashboard/rendez-vous"
        element={
          <RequireAuth>
            <DashboardRendezVousPage />
          </RequireAuth>
        }
      />
      <Route
        path="/dashboard/factures"
        element={
          <RequireAuth>
            <DashboardFacturesPage />
          </RequireAuth>
        }
      />
      <Route
        path="/dashboard/messages"
        element={
          <RequireAuth>
            <DashboardMessagesPage />
          </RequireAuth>
        }
      />
      <Route
        path="/dashboard/assistance"
        element={
          <RequireAuth>
            <DashboardAssistancePage />
          </RequireAuth>
        }
      />
      <Route
        path="/projects/new"
        element={
          <RequireAuth>
            <NewProjectPage />
          </RequireAuth>
        }
      />
      <Route
        path="/projects/:id/wizard"
        element={
          <RequireAuth>
            <ProjectWizardPage />
          </RequireAuth>
        }
      />
      <Route
        path="/projects/:id"
        element={
          <RequireAuth>
            <ProjectDetailPage />
          </RequireAuth>
        }
      />
      {/* Back-office admin + super_admin */}
      <Route
        path="/admin"
        element={
          <RequireRole roles={['admin', 'super_admin']}>
            <AdminOverviewPage />
          </RequireRole>
        }
      />
      <Route
        path="/admin/clients"
        element={
          <RequireRole roles={['admin', 'super_admin']} permission="clients.view">
            <AdminClientsPage />
          </RequireRole>
        }
      />
      <Route
        path="/admin/projects"
        element={
          <RequireRole roles={['admin', 'super_admin']} permission="projects.view">
            <AdminProjectsPage />
          </RequireRole>
        }
      />
      <Route
        path="/admin/projects/:id"
        element={
          <RequireRole roles={['admin', 'super_admin']} permission="projects.view">
            <AdminProjectDetailPage />
          </RequireRole>
        }
      />
      <Route
        path="/admin/clients/:id"
        element={
          <RequireRole roles={['admin', 'super_admin']} permission="clients.view">
            <AdminClientDetailPage />
          </RequireRole>
        }
      />
      <Route
        path="/admin/documents"
        element={
          <RequireRole roles={['admin', 'super_admin']} permission="documents.review">
            <AdminDocumentsPage />
          </RequireRole>
        }
      />
      <Route
        path="/admin/payments"
        element={
          <RequireRole roles={['admin', 'super_admin']} permission="payments.view">
            <AdminPaymentsPage />
          </RequireRole>
        }
      />
      <Route
        path="/admin/packs"
        element={
          <RequireRole roles={['admin', 'super_admin']} permission="packs.manage">
            <AdminPacksPage />
          </RequireRole>
        }
      />
      <Route
        path="/admin/planning"
        element={
          <RequireRole roles={['admin', 'super_admin']} permission="planning.manage">
            <AdminPlanningPage />
          </RequireRole>
        }
      />
      <Route
        path="/admin/partners"
        element={
          <RequireRole roles={['admin', 'super_admin']} permission="partners.manage">
            <AdminPartnersPage />
          </RequireRole>
        }
      />
      <Route
        path="/admin/partners/:id"
        element={
          <RequireRole roles={['admin', 'super_admin']} permission="partners.manage">
            <AdminPartnerDetailPage />
          </RequireRole>
        }
      />
      <Route
        path="/admin/leads"
        element={
          <RequireRole roles={['admin', 'super_admin']} permission="partners.manage">
            <AdminLeadsPage />
          </RequireRole>
        }
      />
      <Route
        path="/admin/interventions"
        element={
          <RequireRole roles={['admin', 'super_admin']} permission="planning.manage">
            <AdminInterventionsPage />
          </RequireRole>
        }
      />
      <Route
        path="/admin/partner-invoices"
        element={
          <RequireRole roles={['admin', 'super_admin']} permission="partners.manage">
            <AdminPartnerInvoicesPage />
          </RequireRole>
        }
      />
      <Route
        path="/admin/appointments"
        element={
          <RequireRole roles={['admin', 'super_admin']} permission="planning.manage">
            <AdminAppointmentsPage />
          </RequireRole>
        }
      />
      <Route
        path="/admin/users"
        element={
          <RequireRole roles={['super_admin']}>
            <AdminUsersPage />
          </RequireRole>
        }
      />
      <Route
        path="/admin/staff"
        element={
          <RequireRole roles={['super_admin']}>
            <AdminStaffPage />
          </RequireRole>
        }
      />
      <Route
        path="/admin/audit"
        element={
          <RequireRole roles={['super_admin']}>
            <AdminAuditPage />
          </RequireRole>
        }
      />
      <Route
        path="/admin/assistance"
        element={
          <RequireRole roles={['admin', 'super_admin']}>
            <AdminAssistancePage />
          </RequireRole>
        }
      />

      {/* Espace partenaire */}
      <Route
        path="/partenaire"
        element={
          <RequireRole roles={['partenaire']}>
            <PartnerProfilePage />
          </RequireRole>
        }
      />
      <Route
        path="/partenaire/leads"
        element={
          <RequireRole roles={['partenaire']}>
            <PartnerLeadsPage />
          </RequireRole>
        }
      />
      <Route
        path="/partenaire/interventions"
        element={
          <RequireRole roles={['partenaire']}>
            <PartnerInterventionsPage />
          </RequireRole>
        }
      />
      <Route
        path="/partenaire/rendez-vous"
        element={
          <RequireRole roles={['partenaire']}>
            <AssigneeAppointmentsPage />
          </RequireRole>
        }
      />
      <Route
        path="/partenaire/assistance"
        element={
          <RequireRole roles={['partenaire']}>
            <PartnerAssistancePage />
          </RequireRole>
        }
      />

      {/* Espace technicien */}
      <Route
        path="/technicien"
        element={
          <RequireRole roles={['admin', 'super_admin', 'technicien']}>
            <TechnicianOverviewPage />
          </RequireRole>
        }
      />
      <Route
        path="/technicien/projects"
        element={
          <RequireRole roles={['admin', 'super_admin', 'technicien']}>
            <TechnicianProjectsPage />
          </RequireRole>
        }
      />
      <Route
        path="/technicien/documents"
        element={
          <RequireRole roles={['admin', 'super_admin', 'technicien']}>
            <TechnicianDocumentsPage />
          </RequireRole>
        }
      />
      <Route
        path="/technicien/planning"
        element={
          <RequireRole roles={['admin', 'super_admin', 'technicien']}>
            <TechnicianPlanningPage />
          </RequireRole>
        }
      />
      <Route
        path="/technicien/reports"
        element={
          <RequireRole roles={['admin', 'super_admin', 'technicien']}>
            <TechnicianReportsPage />
          </RequireRole>
        }
      />
      <Route
        path="/technicien/assistance"
        element={
          <RequireRole roles={['admin', 'super_admin', 'technicien']}>
            <TechnicianAssistancePage />
          </RequireRole>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
